import { useMemo, useRef } from "react";
import { Modal } from "antd";
import { MapContainer, TileLayer, Marker, Popup, Polyline, ZoomControl } from "react-leaflet";
import L from "leaflet";
import trans from "../../utils";
import { isRTL } from "../../utils";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const blueIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const greenIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export default function LocationPreviewMap({ open, record, onClose }) {
  const mapRef = useRef(null);

  const prevPos = useMemo(
    () => (record ? [record.previousLatitude, record.previousLongitude] : null),
    [record],
  );
  const proposedPos = useMemo(
    () => (record ? [record.proposedLatitude, record.proposedLongitude] : null),
    [record],
  );

  const center = useMemo(() => {
    if (!prevPos || !proposedPos) return null;
    return [
      (prevPos[0] + proposedPos[0]) / 2,
      (prevPos[1] + proposedPos[1]) / 2,
    ];
  }, [prevPos, proposedPos]);

  const fitBounds = (map) => {
    if (!prevPos || !proposedPos) return;
    const bounds = L.latLngBounds([prevPos, proposedPos]);
    map.fitBounds(bounds, { padding: [50, 50] });
  };

  const handleAfterOpenChange = (visible) => {
    if (visible && mapRef.current) {
      const map = mapRef.current;
      setTimeout(() => {
        map.invalidateSize();
        fitBounds(map);
      }, 100);
    }
  };

  if (!record || !center) return null;

  return (
    <Modal
      open={open}
      title={trans("Location Preview")}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose
      afterOpenChange={handleAfterOpenChange}
    >
      <div style={{ marginBottom: 12, display: "flex", gap: 24, flexWrap: "wrap" }}>
        <div>
          <span style={{ color: "#2b5797", fontWeight: "bold" }}>● </span>
          {trans("Previous Coordinates")}: {record.previousLatitude}, {record.previousLongitude}
        </div>
        <div>
          <span style={{ color: "#2e7d32", fontWeight: "bold" }}>● </span>
          {trans("Proposed Coordinates")}: {record.proposedLatitude}, {record.proposedLongitude}
        </div>
      </div>
      <MapContainer
        center={center}
        zoom={13}
        zoomControl={false}
        scrollWheelZoom
        style={{ height: 400, width: "100%", borderRadius: 8 }}
        ref={(map) => { if (map) mapRef.current = map; }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ZoomControl position={isRTL ? "bottomleft" : "bottomright"} />
        <Marker position={prevPos} icon={blueIcon}>
          <Popup>{trans("Previous Coordinates")}</Popup>
        </Marker>
        <Marker position={proposedPos} icon={greenIcon}>
          <Popup>{trans("Proposed Coordinates")}</Popup>
        </Marker>
        <Polyline
          positions={[prevPos, proposedPos]}
          pathOptions={{ color: "#ff6b6b", dashArray: "8 8", weight: 2 }}
        />
      </MapContainer>
    </Modal>
  );
}
